import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpBackend} from '@angular/common/http';

import { catchError, map, tap } from 'rxjs/operators';
import { Observable, throwError, of, BehaviorSubject, Subject, zip } from 'rxjs';
import { APP_DI_CONFIG, AppConfig } from './../app-config/app-config';

import { DashboardInfoModel, DeviceInfoModel, RoleDevice } from './../shared/models/deviceInfo.model';
import { AppConfigService } from './../app-config/app-config.service';
import { DeviceConfigModel } from './../shared/models/deviceInfo.model'
import { DeviceConfigurationService } from './device-configuration.service';
import { CHANNEL_TAG_TYPE_MAP } from '../shared/constants/global-constants';
import * as JSZip from 'jszip';
import { saveAs } from 'file-saver';


@Injectable({
  providedIn: 'root'
})

export class DeviceInfoService {

  private deviceConfigData: DeviceConfigModel = null;
  private watchUpdate = new BehaviorSubject(this.deviceConfigData);
  private validationStatus = new Subject();
  private _deviceConfigurationData = {};
  private _deviceOverviewURLParams = null;
  private _deviceList;
  public hmiSessionDetails;
  public deviceTabStatus: boolean = false;
  public hmiSessionStarted: boolean = false;
  private onDemandSyncInitiate = new BehaviorSubject<boolean>(false);
  private syncCanceled =  new BehaviorSubject<boolean>(false);
  private notificationsOpen = new BehaviorSubject<boolean>(false);
  private closeHmiActiveSession = new BehaviorSubject<boolean>(false);
  public showHttpLoader: boolean = true;
  private httpWithoutLoader: HttpClient;

  public espAnalyticsData: any;


  private zip = new JSZip();
  constructor(
    private http: HttpClient,
    private httpBackend: HttpBackend,
    private appConfigService: AppConfigService,
    private configurationService: DeviceConfigurationService
  ) { 
    this.httpWithoutLoader = new HttpClient(httpBackend);
  }

  public getOnDemandSyncInitiate(): Observable<boolean> {
    return this.onDemandSyncInitiate.asObservable();
  }

  setOnDemandSyncInitiate = (val: any) => {
    return this.onDemandSyncInitiate.next(val);
  }

  public getSyncCanceled(): Observable<boolean> {
    return this.syncCanceled.asObservable();
  }

  setSyncCanceled = (cancelVal: any) => {
    return this.syncCanceled.next(cancelVal);
  }

  public getNotificationsOpen(): Observable<boolean> {
    return this.notificationsOpen.asObservable();
  }

  setNotificationsOpen = (val: any) => {
    return this.notificationsOpen.next(val);
  }

  
  public getCloseHmiActiveSession(): Observable<boolean> {
    return this.closeHmiActiveSession.asObservable();
  }

  setCloseHmiActiveSession = (val: any) => {
    return this.closeHmiActiveSession.next(val);
  }

  /**
   * Get | Set for device overview page url params
   */

  set deviceOverviewURLParams(data) {
    this._deviceOverviewURLParams = data;
  }

  get deviceOverviewURLParams() {
    return this._deviceOverviewURLParams;
  }


  /**
   * Get | Set for device configuarion data
   */
  set deviceConfigurationData(data) {
    this._deviceConfigurationData = data;
  }

  get deviceConfigurationData() {
    return this._deviceConfigurationData;
  }

  /**
   * Watch for device config data changes
   */
  getDeviceConfigData(): Observable<DeviceConfigModel> {
    return this.watchUpdate.asObservable();
  }
  

  updateDeviceConfigData(val: DeviceConfigModel): void {
    this.deviceConfigurationData = val;
    this.watchUpdate.next(val);
  }

  /**
   * Watch for device configuration validation status changes
   */
  getValidationStatus() {
    return this.validationStatus.asObservable();
  }

  setValidationStatus(val: boolean) {
    this.validationStatus.next(val);
  }

  /**
   * Get devices
   */
  getDevicesInfo(): Observable<DashboardInfoModel> {
    const params = {
      "searchText": "string",
      "pageSize": 0,
      "currentIndex": 0,
      "sortBy": "string"
    };
    return this.http.post<DashboardInfoModel>(this.appConfigService.getConfig().apiBaseUrl + 'Devices', params);
  }

  /**
   * Get device by ID
   */
  getDeviceById(id: string) {
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + `Device/${id}`);
  }

  /**
   * Get realstream data for device
   * @param deviceId 
   */
  getRealStreamDataForDevice(deviceId) {
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'StreamsStateByNodeId/' + deviceId);
  }

  /**
 * Get device configuration
 */
  getDeviceDetailInfo(deviceId, tagFilter = false) {
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'DeviceConfiguration/' + deviceId)
      .pipe(
        map((deviceDetail: DeviceConfigModel) => {
          this.saveConfigurationDataInService(deviceDetail);
          this.updateDeviceConfigData(deviceDetail);
          let streamDeviceData = { equipmentDetails: [], channelDetails: [], sourceTags: [], eventTags: [], realtimeStream: {}};
          if (deviceDetail && deviceDetail.nodeConfig && deviceDetail.nodeConfig.attachedEquipments && deviceDetail.nodeConfig.attachedEquipments.length && deviceDetail.nodeConfig.attachedEquipments[0]
            && deviceDetail.nodeConfig.attachedEquipments.length && deviceDetail.nodeConfig.attachedEquipments[0].equipmentTemplateSystem
            && deviceDetail.nodeConfig.attachedEquipments[0].equipmentTemplateSystem.tags) {
            let channelType = deviceDetail.nodeConfig.attachedEquipments[0].equipmentTemplateSystem.tagSourceType;
            let tagSourceType = CHANNEL_TAG_TYPE_MAP[channelType];
            // streamDeviceData.equipmentDetails = deviceDetail.nodeConfig.attachedEquipments[0].equipmentTemplateSystem.tags[tagSourceType];
            streamDeviceData.equipmentDetails = this.getEquipmentDetails(deviceDetail, tagSourceType, tagFilter)
            if (deviceDetail.nodeConfig.nodeChannels) {
              streamDeviceData.channelDetails = deviceDetail.nodeConfig.nodeChannels;
            }
            if (deviceDetail.nodeConfig?.realtimeStream) {
              streamDeviceData.realtimeStream = deviceDetail.nodeConfig?.realtimeStream

            }

            return streamDeviceData;
          } else {
            return null;
          }
        }),
        catchError((error) => {
          return null;
        })
      );
  }

  private getEquipmentDetails(deviceDetails: any, tagSourceType: string, tagFilter: boolean) {
    let attachedEquipments = deviceDetails.nodeConfig.attachedEquipments.slice();
    let tagDetails = [];
    let equipmentDetails = [];
    for (let i = 0; i < attachedEquipments.length; i++) {
      let equipmentTemplateSystem = attachedEquipments[i].equipmentTemplateSystem;
      tagSourceType = CHANNEL_TAG_TYPE_MAP[equipmentTemplateSystem.tagSourceType];
      let equipmentId = equipmentTemplateSystem.equipmentId;
      let equipment = attachedEquipments.filter(item => item.equipment.equipmentId === equipmentId);
      if (equipment && equipment[0]) {
        let validUpdatedTags = this.getUpdatedValidTags(equipment[0], tagSourceType);
        tagDetails = [...tagDetails, ...validUpdatedTags];
      }
    }


    equipmentDetails.push(...tagDetails);

    //merge channelinfo
    let channelDetails = JSON.parse(JSON.stringify(deviceDetails.nodeConfig.nodeChannels));
    equipmentDetails = equipmentDetails.map(eqp => {
      let eqpChannel = channelDetails.filter(ch => ch.equipmentId === eqp.equipmentId);
      if (eqpChannel && eqpChannel[0]) {
        return { ...eqp, ...eqpChannel[0], ...{ channelName: eqpChannel[0].nodeChannelType } };
      }
      return eqp;
    })

    return equipmentDetails;
  }

  /**
  * Get updated tag info and remove disabled tags 
  */
  getUpdatedValidTags(equipment: any, tagType: string) {
    let systemTags = equipment.equipmentTemplateSystem.tags[tagType];
    let nodeTags = equipment.equipmentTemplateNode?.tags[tagType];
    systemTags = systemTags.map(sTag => {
      if (nodeTags && nodeTags.length) {
        let nTag = nodeTags.filter(nTag => nTag.tagName === sTag.tagName);
        if (nTag && nTag.length) {
          sTag = nTag[0];
        }
      }
      sTag.equipmentName = equipment.equipment.name;
      return sTag;
    }).filter(t => t.isEnabled === true)

    return systemTags;
  }

  /**
   * Save device configuration
   */
  saveDeviceConfiguration(formData: any) {
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'DeviceConfiguration/', formData)
  }

  /**
   * Get Equpment list
   */
  getEquipmentList() {
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'Equipment');
  }

  /**
   * Get template list
   */
  getTemplateList(equipmentid) {
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'EquipmentTemplatesByEquipmentId/' + equipmentid);
  }


  /**
   * Sort attched Equipment list
   */
  sortAttachedEquipments(list: any, destinamionObjName: string, sortKey: string) {
    if (!list || list.length === 0) return [];

    return list.sort((a: object, b: object) => (a[destinamionObjName][sortKey] > b[destinamionObjName][sortKey]) ? 1 : -1)
  }

  /**
   * Save configuration data in service
   */
  saveConfigurationDataInService(deviceDetail: DeviceConfigModel | any) {
    this.configurationService.resetAllStates();
    if (deviceDetail && deviceDetail.nodeConfig && deviceDetail.nodeConfig.attachedEquipments && deviceDetail.nodeConfig.attachedEquipments.length) {
      this.configurationService.attachedEquipments = deviceDetail.nodeConfig.attachedEquipments;
    }
    this.configurationService.realtimeStream = deviceDetail.nodeConfig?.realtimeStream;

    if (deviceDetail && deviceDetail.nodeConfig && deviceDetail.nodeConfig.nodeChannels && deviceDetail.nodeConfig.nodeChannels.length) {
      this.configurationService.nodeChannels = deviceDetail.nodeConfig.nodeChannels;
      this.configurationService.nodeId = deviceDetail.nodeConfig.parentId;
      this.configurationService.nodeConfigId = deviceDetail.nodeConfig.nodeConfigId;
    } else if (deviceDetail && deviceDetail.nodeId && !deviceDetail.nodeConfig) {
      this.configurationService.nodeId = deviceDetail.nodeId;
    }

    if (deviceDetail) {
      let dataNeeded = JSON.parse(JSON.stringify(deviceDetail));
      if (dataNeeded.hasOwnProperty('nodeConfig')) {
        delete dataNeeded.nodeConfig;
      }
      this.configurationService.deviceOverview = dataNeeded;
    }
  }

  addRealStreamDataToEquipmentList(realStreamData, equipmentDetails) {
    if (realStreamData.length > 0) {
      for (let i = 0; i < realStreamData.length; i++) {
        let tagId = realStreamData[i]['tagId'];
        let equipment = equipmentDetails.filter(item => item.tagId === tagId);
        if (equipment && equipment[0]) {
          for (let key in realStreamData[i]) {
            if (!equipment[0][key]) {
              equipment[0][key] = realStreamData[i][key];
            }
          }
        }
      }
    }
  }

  getUniqueKey(eqp) {
    let key = (eqp.equipment.name + eqp.equipmentTemplateSystem.name).replace(/\s/g, '')
    return key.toLowerCase();
  }

  get deviceList() {
    return this._deviceList;
  }

  set deviceList(data) {
    this._deviceList = data;
  }


  getAllDevices(): Observable<any[]> {
    return this.http.get<DeviceInfoModel[]>(this.appConfigService.getConfig().apiBaseUrl + 'Devices/Minimal');
  }


  getAssignedDevicesToRole(id): Observable<any> {
    return this.http.get<RoleDevice>(this.appConfigService.getConfig().apiBaseUrl + 'Role/' + id + '/Device');
  }


  /**
   * Assign Devices to Role
   */
  saveDevicesAssignedToRole(formData: any) {
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'Role/Device', formData)
  }

  public setDeviceTabStatus(value: boolean) {
    this.deviceTabStatus = value;
  }

  public getDeviceTabStatus() {
    return this.deviceTabStatus
  }

  public setHmiSessionStarted(value: boolean) {
    this.hmiSessionStarted = value;
  }

  public getHmiSessionStarted() {
    return this.hmiSessionStarted;
  }

  public setShowHttpLoader(value: boolean) {
    this.showHttpLoader = value;
  }

  public getShowHttpLoader() {
    return this.showHttpLoader;
  }

  public setHmiSessionDetails(value: any) {
    this.hmiSessionDetails = value;
  }

  public getHmiSessionDetails() {
    return this.hmiSessionDetails;
  }

  getTagsValueAndTimeStamp(formData: any): Observable<any> {
    return this.http.post<RoleDevice>(this.appConfigService.getConfig().apiBaseUrl + 'TagData/Query', formData);
  }


  /**
   * Get latest tag data
   * @returns 
   */
  getLatestTagData() {
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + `TagData/latest`);
  }

  generateDemandScanReqId(formData: any): Observable<any> {
    return this.http.post<RoleDevice>(this.appConfigService.getConfig().apiBaseUrl + 'Command/DemandScan', formData);
  }

  getDemandScanStatusAndValue(id): Observable<any> {
    return this.http.get<RoleDevice>(this.appConfigService.getConfig().apiBaseUrl + 'Command/DemandScan/' + id + '/Status');
  }

  setControlDataPoints = (formData: any) => {
    return this.http.post<RoleDevice>(this.appConfigService.getConfig().apiBaseUrl + 'Command/WriteSetpoint', formData);
  }

  getSetDataPointsStatus(id): Observable<any> {
    return this.http.get<RoleDevice>(this.appConfigService.getConfig().apiBaseUrl + 'Command/WriteSetpoint/' + id + '/Status');
  }

  cancelDemandScanRequest = (formData: any) => {
    return this.http.post<any>(this.appConfigService.getConfig().apiBaseUrl + 'Command/DemandScan/Cancel',formData, {observe:'response'} );
  }

  cancelSetDataPointsRequest = (formData: any) => {
    return this.http.post<any>(this.appConfigService.getConfig().apiBaseUrl + 'Command/WriteSetpoint/Cancel', formData,{ observe: 'response'});
  }

  downloadTagsData(payload: any, dataType: string, deviceName) {
    this.zip = new JSZip();
    let pageIndex =0;
    this.callDownloadApi(payload, dataType,pageIndex,deviceName, true);
  }

  callDownloadApi(payload: any, dataType: string,pageIndex: number,deviceName: string, initial: boolean) {
    payload['pageIndex'] = pageIndex; 
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'TagData/QueryExport?fileType=' + dataType,payload, { responseType: 'blob' as 'blob', observe: 'response' })
    .subscribe(response => {
      var filename = 'data';
      var contentDisposition = response.headers.get('content-disposition');
      var filename = contentDisposition?.split(';')[1].split('filename')[1].split('=')[1].trim();
      if(!filename?.includes('0')){
        pageIndex ++;
      } else {
        pageIndex = 0;
      }
      this.downloadFile(response.body, filename,dataType,pageIndex, deviceName, payload, initial);
  });
  }
  downloadFile(data: any, filename: string, dataType: string, pageIndex: number, deviceName, payload, initial: boolean) {
    let today = new Date();
    let blob: any;
    let downloadName = deviceName + '_' + today.toString().trim().replace(':','');
    if(dataType === 'excel') {
      blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    }
    if(dataType === 'csv') {
      blob = new Blob([data],{type:'text/csv'});  
    }

    if(initial && pageIndex === 0) {
      let link = document.createElement("a");
      if (link.download !== undefined) {
          let url = URL.createObjectURL(blob);
          link.setAttribute("href", encodeURI(url));
          link.setAttribute("download", (dataType === 'excel') ?  downloadName + ".xlsx" : downloadName + ".csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      }
    } else {
      this.zip.file((dataType === 'excel') ?  downloadName + '_' + pageIndex +  ".xlsx" : downloadName + '_' + pageIndex +  ".csv" , blob);
      if(pageIndex !== 0) {
        this.callDownloadApi(payload, dataType,pageIndex, deviceName, false);
      }
  
      if(!initial && pageIndex === 0 ) {
        this.zip.generateAsync({ type: "blob" })
          .then(blobData => saveAs(blobData,downloadName + '.zip'));
      }
    }

  }
  publishOnDemandSync(syncData) {
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'TagData/OnDemandDataPublish', syncData);
  }

  getOnDemandSyncStatus(userId, requestId) {
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'TagData/OnDemandDataPublish/' + userId + '/' + requestId + '/Status');
  }

  getOnDemandDataRequests(userId){
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'TagData/OnDemandDataRequests/' + userId);
  }

  cancelDemandSyncRequest(requestData){
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'TagData/OnDemandDataPublish/Cancel', requestData);
  }

  getDemandSyncDeviceStatus(userId, deviceId, equipmentId){
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'TagData/OnDemandDataPublishByDeviceId/'+ userId +'/' + deviceId + '/' + equipmentId + '/Status');
  }

  updateSyncStatus(syncData){
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'TagData/OnDemandDataPublish/Status', syncData);
  }

  saveTrendTemplate(trendTemplate){
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'TrendTemplate', trendTemplate, {responseType: 'text' as 'json'});
  }

  getAllTrendTemplates(){
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'TrendTemplate/GetAllTemplates');
  }

  deleteTrendTemplate(id){
    return this.http.delete(this.appConfigService.getConfig().apiBaseUrl + 'TrendTemplate/'+ id, {responseType: 'text'});
  }

  openHmiSession(deviceId,userId){
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'HmiSession/StartHmiSession/'+deviceId+'/'+userId,'' );
  }

  saveHmiSession(payload){
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'HmiSession/SaveHMIDetails', payload);
  }

  closeHmiSession(payload){
    this.setHmiSessionStarted(false);
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'HmiSession/EndHmiSession ', payload);
  }

  getRunningAnalytics(deviceId){
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'Analytic/RunningAnalytics/' +deviceId );
  }

  getEspAnalyticsConfig(deviceId){
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'Analytic/GetEspStabilityAnalyticConfiguration/' +deviceId );
  }

  runAnalyticsOndemand(deviceId, payload){
    return this.http.post(this.appConfigService.getConfig().apiBaseUrl + 'Analytic/EspStabilityOnDemandDataCalculation/'+deviceId, payload );
  }

  getHmiConnectionStatus(sessionId){
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'HmiSession/GetHmiConnectionStatus/'+sessionId );
  }

  getCellModemStrength(deviceId){
    const token = sessionStorage.getItem('access_token'); // Or get from a real AuthService
 
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
    return this.httpWithoutLoader.get(this.appConfigService.getConfig().apiBaseUrl + 'GetSignalStrength/'+deviceId, {responseType: 'text' as 'json', headers:headers })
  }

  getActiveHmiSessionStatus(sessionId){
    return this.http.get(this.appConfigService.getConfig().apiBaseUrl + 'HmiSession/CheckSessionBySessionId/'+sessionId);
  }

}